import axios from "axios";

const api = axios.create({
  // (2026-05-15) baseURL configurable por entorno. Por defecto "" =
  // mismo origen: en dev lo resuelve el proxy de package.json; en el
  // build self-hosted detrás de IIS, IIS hace reverse-proxy de
  // /api y /admin hacia Waitress (:51500). Si algún día se publica en
  // Vercel, setear REACT_APP_API_BASE_URL="/api/proxy" en el .env.
  baseURL: process.env.REACT_APP_API_BASE_URL || "",
  headers: {
    "Content-Type": "application/json",
    // (2026-05-13) Marca que la solicitud proviene de la capa web del SaaS.
    // El backend rechaza logins de usuarios con es_integrador=True cuando
    // este header no es 'integracion'. Las integraciones externas (cURL,
    // ERPs) deben enviar 'integracion' explicitamente.
    "X-Client-App": "web",
  },
});

// Agregar token a cada request
api.interceptors.request.use(
  (config) => {
    // (2026-05-13) Asegurar que X-Client-App siempre viaje aunque algun
    // axios.create personalizado o un override en runtime lo elimine.
    if (!config.headers["X-Client-App"]) {
      config.headers["X-Client-App"] = "web";
    }
    const authData = localStorage.getItem("authData");
    if (authData) {
      const { token } = JSON.parse(authData);
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// (2026-05-16 Req#3) Capturar token refrescado por el backend (refresco
// deslizante). Si la respuesta trae 'X-Refreshed-Token', se actualiza el
// token guardado sin interrumpir al usuario. Aditivo: si no viene, no pasa nada.
function _applyRefreshedToken(response) {
  try {
    const fresh =
      response &&
      response.headers &&
      (response.headers["x-refreshed-token"] ||
        response.headers["X-Refreshed-Token"]);
    if (fresh) {
      const raw = localStorage.getItem("authData");
      if (raw) {
        const data = JSON.parse(raw);
        data.token = fresh;
        localStorage.setItem("authData", JSON.stringify(data));
      }
    }
  } catch (e) {
    /* nunca romper el flujo por el refresco */
  }
  return response;
}

// Manejar 401 globalmente
api.interceptors.response.use(
  (response) => _applyRefreshedToken(response),
  (error) => {
    // (2026-05-19) Compatibilidad central con el envelope de error
    // estandarizado del backend: { estado:"error", codigo:<int>,
    // mensaje:"..." , detalles?:... }. Históricamente los componentes
    // leen `err.response.data.error`. Para no editar decenas de catch,
    // se aliasa `error <- mensaje` (y se conserva mensaje/codigo/estado
    // para el código nuevo). Aditivo y defensivo.
    try {
      const d = error.response && error.response.data;
      if (d && typeof d === "object") {
        if (d.error == null && typeof d.mensaje === "string") {
          d.error = d.mensaje;
        }
        if (d.mensaje == null && typeof d.error === "string") {
          d.mensaje = d.error;
        }
      }
    } catch (_e) {
      /* noop: nunca romper el manejo de error por el shim */
    }
    // (2026-07-05) Contraseña vencida a mitad de sesión: el backend devuelve 403
    // con codigo 'PASSWORD_EXPIRED' en cualquier mutación de operador/operador_admin.
    // Se redirige al cambio obligatorio (mismo flujo que must_change_password).
    if (
      error.response?.status === 403 &&
      error.response?.data?.codigo === "PASSWORD_EXPIRED"
    ) {
      if (!window._pwdExpiredRedirect) {
        window._pwdExpiredRedirect = true;
        import("react-toastify").then(({ toast }) => {
          toast.error("Tu contraseña ha vencido. Debes cambiarla para continuar.", {
            position: "top-center",
            autoClose: 2500,
            onClose: () => {
              window._pwdExpiredRedirect = false;
              window.location.href = "/profile?forceChangePassword=true";
            },
          });
        });
      }
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      // Evitar mostrar múltiples toasts si se disparan varios 401 a la vez
      if (!window._sessionExpired) {
        window._sessionExpired = true;

        // Mostrar notificación
        import("react-toastify").then(({ toast }) => {
          toast.error("Tu sesión ha expirado. Por favor inicia sesión nuevamente.", {
            position: "top-center",
            autoClose: 2000,
            onClose: () => {
              localStorage.removeItem("authData");
              window._sessionExpired = false;
              window.location.href = "/login";
            },
          });
        });
      }
    }
    return Promise.reject(error);
  }
);

// (2026-05-16 Req#3) Detector de actividad: mientras el usuario esté activo,
// se hace un ping liviano periódico a un endpoint autenticado para que el
// backend dispare el refresco deslizante (header X-Refreshed-Token) ANTES
// del vencimiento. Sin actividad => sin pings => el token expira (seguridad).
if (typeof window !== "undefined") {
  let _lastActivity = Date.now();
  let _lastPing = 0;
  const ACTIVE_WINDOW_MS = 5 * 60 * 1000;   // "activo" = interactuó últimos 5 min
  const PING_EVERY_MS = 4 * 60 * 1000;      // a lo sumo 1 ping cada 4 min
  const CHECK_EVERY_MS = 60 * 1000;         // chequeo cada 60 s

  const _markActivity = () => {
    _lastActivity = Date.now();
  };
  ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"].forEach(
    (ev) =>
      window.addEventListener(ev, _markActivity, { passive: true, capture: true })
  );

  setInterval(() => {
    try {
      const raw = localStorage.getItem("authData");
      if (!raw) return; // no logueado
      const now = Date.now();
      const activo = now - _lastActivity < ACTIVE_WINDOW_MS;
      if (activo && now - _lastPing >= PING_EVERY_MS) {
        _lastPing = now;
        // Ping liviano; el interceptor de respuesta aplicará el token
        // refrescado si el backend lo emite. Errores se ignoran (el 401
        // global ya maneja sesión realmente expirada).
        api.get("/api/profile").catch(() => {});
      }
    } catch (e) {
      /* noop */
    }
  }, CHECK_EVERY_MS);
}

export default api;
